import http from 'axios';
import Recaptcha from 'recaptcha/Recaptcha';

const httpHeaders = {
    common: {
        'X-Requested-With': 'XMLHttpRequest'
    }
};

const debug = false;

function buildFormData(formData, data, baseKey) {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
        Object.keys(data).forEach((key) => {
            buildFormData(formData, data[key], baseKey ? `${baseKey}[${key}]` : key);
        });
    } else {
        formData.append(baseKey, data);
    }
}

export default class HttpService {
    constructor() {
        this.regionBaseUrl = '';
        this.regionBaseUrl = CBS.Registry.region.prefix;
    }

    /**
     *
     * @param url
     * @param data
     * @returns {Promise<HttpServiceResponse>}
     */
    async doPost(url, data) {
        let self = this;
        let r = new HttpServiceResponse();

        // get recaptcha token if data.recaptchaAction is provided
        if (data && data?.recaptchaAction) {
            await Recaptcha.appendRecaptchaToken(data);
        }

        // form data
        let formData = new FormData();

        buildFormData(formData, data);

        return new Promise(function (resolve, reject) {
            if (typeof data !== 'object') {
                r.setSuccess(false).setMessage('submitted data is not an object');

                resolve(r.getResponse());

                return;
            }

            http.post(self.regionBaseUrl + url, formData, {
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
                headers: httpHeaders
            })
                .then((res) => {
                    if (debug) {
                        console.warn('raw res', res);
                    }

                    r.handleResponse(res);

                    // track recaptcha validation from api response
                    Recaptcha.trackRecaptchaTokenValidationResult(res);

                    resolve(r.getResponse());
                })
                .catch((err) => {
                    r.handleError(err);

                    reject(r.getResponse());
                });
        });
    }

    /**
     *
     * @param url
     * @param data
     * @returns {Promise<HttpServiceResponse>}
     */
    doGet(url, data) {
        let self = this;
        let r = new HttpServiceResponse();

        let params = {
            headers: httpHeaders
        };

        if (data) {
            params.params = data;
        }

        return new Promise(function (resolve, reject) {
            http.get(self.regionBaseUrl + url, params)
                .then((res) => {
                    r.handleResponse(res);

                    resolve(r.getResponse());
                })
                .catch((err) => {
                    r.handleError(err);

                    reject(r.getResponse());
                });
        });
    }
}

class HttpServiceResponse {
    constructor() {
        this.success = '';
        this.message = '';
        this.data = {};
        this.errors = {};

        // backend error messages
        this.errorMessages = {};
    }

    setSuccess(bool) {
        this.success = bool;
        return this;
    }

    getSuccess() {
        return this.success;
    }

    setMessage(msg) {
        this.message = msg;
        return this;
    }

    getMessage() {
        return this.message;
    }

    setData(resData) {
        let excludeKeys = ['success', 'message'];

        if (typeof resData !== 'object') {
            if (debug) {
                console.warn(
                    'HttpServiceResponse:setData did not receive an object. You need to fix, maybe you got an API error?'
                );
            }
        } else {
            this.data = resData;
            for (let key in resData) {
                if (resData.hasOwnProperty(key) && excludeKeys.indexOf(key) === -1) {
                    this.data[key] = resData[key];
                }
            }
        }
    }

    getData() {
        return this.data;
    }

    setError(message) {
        this.setSuccess(false).setMessage(message);
    }

    getErrors() {
        return this.errors;
    }

    getErrorMessages() {
        return this.errorMessages;
    }

    setErrors(obj) {
        this.errors = obj;
    }

    /**
     * backend error messages
     *
     * @param obj
     */
    setErrorMessages(obj) {
        this.errorMessages = obj;
    }

    handleResponse(res) {
        if (typeof res !== 'object') {
            this.setError('response was not an object');
            return;
        }

        if (typeof res.data === 'undefined') {
            this.setError('no data in the response');
            return;
        }

        if (typeof res.data !== 'object') {
            this.setError('response data is not an object: ', res.data);
            return;
        }

        if (typeof res.data.success === 'undefined') {
            this.setError('success was not provided in the response');
            return;
        }

        if (!res.data.success) {
            this.setError(res.data.message ? res.data.message : 'no error provided');
            this.setErrors(res.data.errors);
            if (res.data.errorMessages) {
                this.setErrorMessages(res.data.errorMessages);
            }
            this.setData(res.data);
            return;
        }

        this.setSuccess(true)
            .setMessage(res.data.message ? res.data.message : '')
            .setData(res.data);
    }

    handleError(err) {
        this.setSuccess(false).setMessage(err.message);
    }

    /**
     *
     * @returns {{data: *, success: *, message: *, errors: *}}
     */
    getResponse() {
        return {
            success: this.getSuccess(),
            message: this.getMessage(),
            data: this.getData(),
            errors: this.getErrors(),
            errorMessages: this.getErrorMessages()
        };
    }
}
